import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import ParticleCursor from "../components/site/ParticleCursor";
import Reveal from "../components/site/Reveal";
import JournalArtwork from "../components/journal/JournalArtwork";
import JournalCard from "../components/journal/JournalCard";
import JournalSeo from "../components/journal/JournalSeo";
import JournalClosingCard from "../components/journal/JournalClosingCard";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import SignalGuidanceCard from "../components/site/SignalGuidanceCard";
import { getPostBySlug, getRelatedPosts } from "../data/journalPosts";
import { recordJournalRead, signalForTags } from "../lib/signalPathEngine";
import { trackEvent } from "../lib/track";

const NotFound = () => (
  <main
    data-testid="journal-not-found"
    className="min-h-screen bg-themed flex items-center justify-center px-6"
  >
    <div className="text-center max-w-md">
      <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-4">
        The Momentum Journal
      </p>
      <h1 className="font-serif text-3xl text-themed mb-4">
        This reflection has not been written yet.
      </h1>
      <a href="/journal" className="btn-gold inline-block mt-6">
        Return to the Journal
      </a>
    </div>
  </main>
);

const JournalPostPage = ({ slug }) => {
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) {
      trackEvent("journal_post_viewed", { slug: post.slug, primary_path: post.primaryPath });
      recordJournalRead(post.slug, post.themeTags);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [post]);

  if (!post) {
    return (
      <>
        <Navigation />
        <NotFound />
        <Footer />
      </>
    );
  }

  const related = getRelatedPosts(post.slug, 3);

  return (
    <>
      <JournalSeo post={post} />
      <ParticleCursor />
      <Navigation />
      <main
        className="min-h-screen bg-themed noise-overlay pt-32 pb-28"
        data-testid="journal-post"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {/* Crumb / back */}
          <nav
            aria-label="Breadcrumb"
            className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mb-8"
            data-testid="journal-breadcrumb"
          >
            <a href="/journal" className="hover:text-gold inline-flex items-center gap-1.5">
              <ArrowLeft size={11} /> The Momentum Journal
            </a>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {post.themeTags.map((t) => (
                <span key={t} className="journal-tag">{t}</span>
              ))}
              <span className="text-themed-muted text-[11px]">·</span>
              <span className="text-themed-muted text-[11px] tracking-[0.25em] uppercase">
                {post.readingTime}
              </span>
            </div>
            <h1
              className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-[1.1]"
              data-testid="journal-post-title"
            >
              {post.title}
            </h1>
            <div className="mt-6 flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-themed-muted">
              <span>By Cyrus Prescod</span>
              <span className="text-themed/30">·</span>
              <time dateTime={post.publishedAt}>
                {new Date(post.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </time>
            </div>
          </header>

          {/* Featured artwork */}
          <Reveal delay={80}>
            <div className="mb-12">
              <JournalArtwork motif={post.featuredImage.motif} alt={post.featuredImage.alt} />
            </div>
          </Reveal>

          {/* Direct answer block (AEO) */}
          <Reveal delay={120}>
            <aside
              className="journal-answer mb-12"
              data-testid="journal-direct-answer"
              aria-label="Short truth"
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
                {post.directAnswerLabel}
              </p>
              <p className="text-themed text-base md:text-lg leading-relaxed">
                {post.directAnswer}
              </p>
            </aside>
          </Reveal>

          {/* Opening paragraphs */}
          <article className="journal-prose" data-testid="journal-body">
            {post.opening.map((p, i) => (
              <p key={`o-${i}`}>{p}</p>
            ))}

            {/* Body sections */}
            {post.bodySections.map((s) => (
              <section key={s.heading}>
                <h2 className="font-serif text-2xl md:text-3xl text-themed mt-12 mb-5 leading-tight">
                  {s.heading}
                </h2>
                {s.paragraphs.map((p, i) => (
                  <p key={`p-${i}`}>{p}</p>
                ))}
              </section>
            ))}
          </article>

          {/* Reflection question — only rendered when the post defines one
              (Volume II). Sits between the body and the closing card so
              the reader leaves with something specific to sit with. */}
          {post.reflectionQuestion && (
            <Reveal delay={120}>
              <aside
                className="mt-16 mb-4 border-t border-gold/15 pt-10"
                data-testid="journal-reflection-question"
              >
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-4">
                  One question to sit with
                </p>
                <p className="font-serif text-themed text-xl md:text-2xl leading-snug italic">
                  {post.reflectionQuestion}
                </p>
              </aside>
            </Reveal>
          )}

          {/* Closing — the original conversion card, then a signal-aware
              bridge tuned to the post's tag-signal. The site lets the
              reader's own reflection point the next step. */}
          <JournalClosingCard post={post} />
          {(() => {
            const tagSignal = signalForTags(post.themeTags);
            // Only render the second bridge when the tag-signal is
            // different from the post's hard-coded primaryPath so we
            // don't show two cards saying the same thing.
            if (!tagSignal || tagSignal === post.primaryPath) return null;
            return (
              <SignalGuidanceCard
                placement="journal-end"
                forceSignal={tagSignal}
                className="mb-12"
              />
            );
          })()}

          {/* FAQ */}
          {post.faqs && post.faqs.length > 0 && (
            <section
              className="mt-12 border-t border-gold/10 pt-12"
              data-testid="journal-faq"
              aria-label="Questions worth asking"
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-6">
                Questions worth asking
              </p>
              <div className="space-y-7">
                {post.faqs.map((f) => (
                  <div key={f.q}>
                    <h3 className="text-themed text-base md:text-lg leading-snug font-medium">
                      {f.q}
                    </h3>
                    <p className="text-themed text-sm md:text-base mt-2 leading-relaxed">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div className="max-w-6xl mx-auto px-6 md:px-10 mt-28">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-8">
              Continue the reflection
            </p>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              data-testid="journal-related"
            >
              {related.map((p) => (
                <JournalCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default JournalPostPage;
